import { message } from 'antd'
import { UploadFile } from 'antd/lib/upload/interface'
import Upload from 'antd/lib/upload/Upload'
import classNames from 'classnames'
import _ from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { uploadQiNiu } from '~/apis/upload-oss'
import { MyIcon } from '~/components/ui/MyIcon'
import { FileType } from '~/configs'
import { OssBucket } from '~/graphqls/types'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { ExtendsUploadFile, useUpload } from '~/hooks/useUpload'
import { trimFileExt } from '~/utils'

import styles from './index.module.css'
import UploadListItem from './UploadListItem'

export const UploadPlaceholder = ({
  label,
  canRename,
}: {
  label?: React.ReactNode
  canRename: boolean
}) => {
  return (
    <div
      className={classNames(
        'border border-neutral-600 rounded-2xl p-3 w-[152px]',
        'upload-placeholder'
      )}
    >
      <div className="rounded-lg bg-neutral-700 flex flex-col items-center justify-center w-32 h-32">
        <span
          className={classNames(
            'text-neutral-400 w-6 h-6 block',
            styles.placeholderIcon
          )}
        >
          <MyIcon icon="plussquare" className="text-[24px]" />
        </span>
        {label && (
          <span
            className={classNames(
              'text-neutral-400 text-xs',
              styles.placeholderLabel
            )}
          >
            {label}
          </span>
        )}
      </div>
      {canRename && <div className="flex items-center h-8"></div>}
    </div>
  )
}

export interface MyUploadFile extends UploadFile {
  attrNode?: React.ReactNode
}

export const MultiFileUpload = ({
  value = [],
  onChange,
  multiple = true,
  maxToken = Infinity,
  remainToken = Infinity,
  disabled = false,
  fileTypes = [FileType.jpg, FileType.png],
  needHash = true,
  bucket = OssBucket.TRADE_PROTECT,
  limit = 50,
  label,
  canRename = true,
  dirname,
  hasAttr = false,
  className,
}: {
  value?: MyUploadFile[]
  onChange?: (value: MyUploadFile[]) => void
  /** 是否支持多选 */
  multiple?: boolean
  /** 最大可上传数量, 到达后隐藏上传部件。ui层控制，不能控制多选的场景 */
  maxToken?: number
  /** 剩余可上传数量。逻辑层控制，适用于可以多选的场景 */
  remainToken?: number
  disabled?: boolean
  fileTypes?: string[]
  needHash?: boolean
  bucket?: OssBucket
  limit?: number
  label?: React.ReactNode
  canRename?: boolean
  dirname?: string
  hasAttr?: boolean
  className?: string
}) => {
  const fileList = value
  const [filenameMap, setFilenameMap] = useState<Record<string, string>>({})
  const handleRenameFile = useCallback(
    (uid: string, value: string) => {
      setFilenameMap(current => {
        return { ...current, [uid]: value }
      })
    },
    [filenameMap]
  )

  const handleFileListChange = useCallback(
    (list: ExtendsUploadFile[]) => {
      list = list.map(file => {
        // 文件名称最大为20
        file.name = (filenameMap[file.uid] || trimFileExt(file.name)).slice(
          0,
          20
        )
        return file
      })
      onChange?.(list)
    },
    [onChange, filenameMap]
  )

  const { getUploadToken, QNUPLOAD, beforeUpload, onUpload } = useUpload({
    limit,
    needHash,
    bucket,
    showList: true,
    onFileListChange: handleFileListChange,
    remainToken,
    fileTypes,
    dirname,
  })

  useDidUpdateEffect(() => {
    // 备注:
    // 1）第一次不渲染是因为调用onChange方法，就会触发表单校验
    // 2）不能将onChange添加在依赖数组里，不然当表单重置时，onChange方法有变动
    handleFileListChange(fileList)
  }, [filenameMap])

  const [willDeleteId, setWillDeleteId] = useState<string>()
  const removeFileFromList = (fid: string) => {
    setWillDeleteId(fid)
  }
  useDidUpdateEffect(() => {
    const newList = fileList.filter(file => file.uid !== willDeleteId)
    onChange?.(newList)
  }, [willDeleteId])

  const handleUpload = async (file: ExtendsUploadFile) => {
    try {
      if (!file.originFileObj) return

      onChange?.(
        fileList.map(item => {
          if (item.uid === file.uid) {
            return { ...item, status: 'uploading', percent: 0 }
          }
          return item
        })
      )

      const { url } = await uploadQiNiu(
        file.originFileObj,
        undefined,
        bucket,
        dirname
      )
      onChange?.(
        fileList.map(item => {
          if (item.uid === file.uid) {
            return {
              ...item,
              status: 'done',
              url,
              percent: 100,
              originFileObj: undefined,
              response: undefined,
            }
          }
          return item
        })
      )
    } catch (err) {
      onChange?.(
        fileList.map(item => {
          if (item.uid === file.uid) {
            return { ...item, status: 'error', percent: 0 }
          }
          return item
        })
      )
      message.destroy()
      message.error('上传异常，请稍后重试')
    }
  }
  const [willReUpload, setWillReUpload] = useState<ExtendsUploadFile>()
  const reUpload = (file: ExtendsUploadFile) => {
    setWillReUpload(file)
  }
  useEffect(() => {
    if (willReUpload) {
      handleUpload(willReUpload)
    }
  }, [willReUpload])

  return (
    <div
      className={classNames(
        styles.upload,
        {
          [styles.uploadDisabled]: remainToken <= 0 || disabled,
          [styles.hiddenPlaceholder]: (value || []).length >= maxToken,
        },
        'multi-upload',
        className
      )}
    >
      <Upload
        action={QNUPLOAD}
        data={getUploadToken}
        listType="picture-card"
        fileList={fileList}
        showUploadList={true}
        beforeUpload={beforeUpload}
        onChange={onUpload}
        multiple={multiple}
        itemRender={(originNode, file, fileList) => {
          return (
            <div className="flex items-start justify-between border border-neutral-600 rounded-2xl">
              <UploadListItem
                file={file}
                filename={filenameMap[file.uid]}
                onRenameFile={handleRenameFile}
                onRemove={removeFileFromList}
                reUpload={reUpload}
                disabled={disabled}
                canRename={canRename}
              />
              {hasAttr && (file as MyUploadFile)?.attrNode}
            </div>
          )
        }}
      >
        <UploadPlaceholder label={label} canRename={canRename} />
      </Upload>
    </div>
  )
}
